import React, { useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';
import { batchSameProducts, comparePlanogramItemGroupsForDisplay } from '../../../common/utils/planograms/planogram';
import { useBooleanState } from '../../../common/utils/hooks';
import { ROLE_PERMISSIONS } from '../../../common/constants';

import { Dialog, Button } from '../../components';
import Text from '../../components/text/Text';
import { useThemedStyles } from '../../hooks/useThemedStyles';

import PickListTotalItems from '../picklist/PickListTotalItems';

import PlanogramDetailFilterDialog from './PlanogramDetailFilterDialog';
import PlanogramDetailItems from './PlanogramDetailItems';

const PlanogramDetail = ({
	items: rawItems,
	totalPickQuantity,
	version,
	pickListItemToggleIsPicked,
	setPickListItemIsPickedBulk,
	isConfirmStockLevels,
	isConfirmValues,
	onClickItem,
	onAddMissingQuantity,
	itemClickPermission = ROLE_PERMISSIONS.ADD_EDIT_PLANOGRAMS,

	defaultSortMethod,
	sortFunction,
	defaultShowAllSelections,
	defaultBatchSameProduct,
	defaultHideImages,
	defaultHideHealthCompliance,
	defaultHidePickedItems,
	defaultShowSalesLastXDaysForPicklists,
	defaultShowExpiryDateButtons,
	defaultShowPickOrderForPicklists,
	defaultShowQuantitySoldAfterRecalc,
	defaultShowExpiryDates,

	defaultShowAdvancedView,
	defaultShowSalesLastXDays,

	machineUsesIngredients = false,
	isMarket = false,

	setDisplayedItems,
	serviceRunPlanned,
	serviceFrequencyDays,
	planogramItemGroups,

	// Optional: control the Settings dialog from the screen header instead of
	// the in-list Settings button.
	isSettingsDialogOpen,
	toggleSettingsDialog,

}) => {
	/*
		Originally, this was a picklist list of items.

		However, it became clear that it should also be used to set inventory;
		this lead to the conclusion that really, both a picklist list of items
		and setting inventory are simply utilising similar versions of what is
		actually a planogram!!

		Hence, this is PlanogramDetail, and it simply displays a list of products
		with passed in options, and with some UI functionality.

	*/


	// Filter settings - default is passed in.
	const [showAllSelections, toggleShowAllSelections] = useBooleanState(defaultShowAllSelections);
	const [hideImages, toggleHideImages] = useBooleanState(defaultHideImages);
	const [hideHealthCompliance, toggleHideHealthCompliance] = useBooleanState(defaultHideHealthCompliance);
	const [sortMethod, setSortMethod] = useState(defaultSortMethod);
	const [batchSameProduct, toggleBatchSameProduct] = useBooleanState(defaultBatchSameProduct);
	const [hidePickedItems, toggleHidePickedItems] = useBooleanState(defaultHidePickedItems);
	const [showAdvancedView, toggleShowAdvancedView] = useBooleanState(defaultShowAdvancedView);
	const [showSalesLastXDays, toggleShowSalesLastXDays] = useBooleanState(defaultShowSalesLastXDays);
	const [showSalesLastXDaysForPicklists, toggleShowSalesLastXDaysForPicklists] = useBooleanState(defaultShowSalesLastXDaysForPicklists);
	const [showPickOrderForPicklists, toggleShowPickOrderForPicklists] = useBooleanState(defaultShowPickOrderForPicklists);
	const [showQuantitySoldAfterRecalc, toggleShowQuantitySoldAfterRecalc] = useBooleanState(defaultShowQuantitySoldAfterRecalc);
	const [showExpiryDates, toggleShowExpiryDates] = useBooleanState(defaultShowExpiryDates);
	const [showExpiryDateButtons, toggleShowExpiryDateButtons] = useBooleanState(defaultShowExpiryDateButtons);
	const [processedItems, setProcessedItems] = useState([]);

	const styles = useThemedStyles(createStyles);
	const [internalDialogIsOpen, toggleInternalDialogIsOpen] = useBooleanState(false);
	const isSettingsControlled = Boolean(toggleSettingsDialog);
	const dialogIsOpen = isSettingsControlled ? Boolean(isSettingsDialogOpen) : internalDialogIsOpen;
	const toggleDialogIsOpen = isSettingsControlled ? toggleSettingsDialog : toggleInternalDialogIsOpen;

	// if normal machine or batching products then items are just normal items
	// otherwise these are planogramItemGroups with field items representing planogram items
	const getRawProcessedItems = useCallback((items) => {

		if (batchSameProduct || !isMarket || !planogramItemGroups) {
			return items;
		}

		// group items by planogram item group
		const groupsWithItems = [];

		// add planogram items to planogram item groups
		for (const planogramItemGroup of planogramItemGroups) {
			const groupObject = {
				...planogramItemGroup,
				items: items.filter(i => i.planogramItemGroupId === planogramItemGroup.id),
			};

			groupsWithItems.push(groupObject);
		}

		return groupsWithItems.sort(comparePlanogramItemGroupsForDisplay);
	}, [planogramItemGroups, batchSameProduct, isMarket]);

	const processItems = useCallback((items, withGroup) => {

		if (version.filterRequiredQuantity && !showAllSelections) {
			// Filter zero selections in picklist version
			items = items.filter(item => {
				// Operates on required quantity (Not required for set inventory, only for picklist)
				return parseInt(item.requiredQuantity, 10) > 0;
			});
		}

		if (hidePickedItems) {
			items = items.filter(item => {
				return item.requiredQuantity > (item.pickedQuantity + item.pickedExtraQuantity);
			});
		}

		// Sort picked inventory
		items = sortFunction(sortMethod, items);

		// Batch products
		if (batchSameProduct) {
			items = batchSameProducts(items);
		}

		if (setDisplayedItems && !withGroup) {
			setDisplayedItems(items);
		}

		return items;
	}, [hidePickedItems, setDisplayedItems, batchSameProduct, version, showAllSelections, sortFunction, sortMethod]);

	// Process the items (apply sorting, batching, etc);
	const getProcessedItems = useCallback((rawProcessedItems) => {

		if (batchSameProduct || !isMarket || !planogramItemGroups) {
			return processItems(rawProcessedItems);
		}
		else {

			const voiceMode = [];
			rawProcessedItems.map(planogramItemGroup => {
				const newItems = processItems(planogramItemGroup?.items, true);
				voiceMode.push(newItems);
				planogramItemGroup.items = newItems;
				return planogramItemGroup;
			});

			const flattedVoiceModeItems = voiceMode.flat();
			if (setDisplayedItems) {
				setDisplayedItems(flattedVoiceModeItems);
			}
		}
		return rawProcessedItems;
	}, [isMarket, planogramItemGroups, processItems, batchSameProduct, setDisplayedItems]);

	useEffect(() => {
		const rawProcessedItems = getRawProcessedItems(rawItems);
		setProcessedItems(getProcessedItems(rawProcessedItems));
	}, [rawItems, getRawProcessedItems, setProcessedItems, getProcessedItems, showAllSelections, hideImages, hideHealthCompliance, sortMethod, batchSameProduct, hidePickedItems, showAdvancedView, showSalesLastXDays, showSalesLastXDaysForPicklists, showPickOrderForPicklists]);

	return (
		<View>
			{
				version.settings && !isSettingsControlled && (
					<View style={styles.settingsButton}>
						<Button
							onPress={toggleDialogIsOpen}
							label="Settings"
						/>
					</View>
				)
			}
			{
				!machineUsesIngredients && (
					<PickListTotalItems
						totalPickQuantity={totalPickQuantity}
					/>
				)
			}
			{
				(batchSameProduct || !isMarket || !planogramItemGroups) ? (
					<PlanogramDetailItems
						items={processedItems}
						showAllSelections={showAllSelections}
						hideImages={hideImages}
						hideHealthCompliance={hideHealthCompliance}
						sortMethod={sortMethod}
						batchSameProduct={batchSameProduct}
						hidePickedItems={hidePickedItems}
						showAdvancedView={showAdvancedView}
						showSalesLastXDays={showSalesLastXDays}
						version={version}
						sortFunction={sortFunction}
						setDisplayedItems={setDisplayedItems}
						itemClickPermission={itemClickPermission}
						onClickItem={onClickItem}
						onAddMissingQuantity={onAddMissingQuantity}
						readOnly={version.readOnly}
						pickListItemToggleIsPicked={pickListItemToggleIsPicked}
						setPickListItemIsPickedBulk={setPickListItemIsPickedBulk}
						isConfirmStockLevels={isConfirmStockLevels}
						isConfirmValues={isConfirmValues}
						isMarket={isMarket}
						showSalesLastXDaysForPicklists={showSalesLastXDaysForPicklists}
						showPickOrderForPicklists={showPickOrderForPicklists}
						showQuantitySoldAfterRecalc={showQuantitySoldAfterRecalc}
						showExpiryDates={showExpiryDates}
						showExpiryDateButtons={showExpiryDateButtons}
						serviceRunPlanned={serviceRunPlanned}
						serviceFrequencyDays={serviceFrequencyDays}
					/>
				) : (
					processedItems?.map(planogramItemGroup => (
						<View key={planogramItemGroup.id}>

							<Text style={styles.groupName} accessibilityRole="header">{planogramItemGroup.name}</Text>
							{ !planogramItemGroup.items?.length ? (
								<Text style={styles.groupEmpty}>No products</Text>
							) : (
								<PlanogramDetailItems
									items={planogramItemGroup.items}
									showAllSelections={showAllSelections}
									hideImages={hideImages}
									hideHealthCompliance={hideHealthCompliance}
									sortMethod={sortMethod}
									batchSameProduct={batchSameProduct}
									hidePickedItems={hidePickedItems}
									showAdvancedView={showAdvancedView}
									showSalesLastXDays={showSalesLastXDays}
									version={version}
									sortFunction={sortFunction}
									setDisplayedItems={setDisplayedItems}
									itemClickPermission={itemClickPermission}
									onClickItem={onClickItem}
									onAddMissingQuantity={onAddMissingQuantity}
									readOnly={version.readOnly}
									pickListItemToggleIsPicked={pickListItemToggleIsPicked}
									setPickListItemIsPickedBulk={setPickListItemIsPickedBulk}
									isConfirmStockLevels={isConfirmStockLevels}
									isConfirmValues={isConfirmValues}
									isMarket={isMarket}
									showSalesLastXDaysForPicklists={showSalesLastXDaysForPicklists}
									showPickOrderForPicklists={showPickOrderForPicklists}
									showQuantitySoldAfterRecalc={showQuantitySoldAfterRecalc}
									showExpiryDates={showExpiryDates}
									showExpiryDateButtons={showExpiryDateButtons}
									serviceRunPlanned={serviceRunPlanned}
									serviceFrequencyDays={serviceFrequencyDays}
								/>
							)}
						</View>

					))
				)
			}
			<Dialog
				isOpen={dialogIsOpen}
				onClose={toggleDialogIsOpen}
			>
				<PlanogramDetailFilterDialog
					showAllSelections={showAllSelections}
					hideImages={hideImages}
					hideHealthCompliance={hideHealthCompliance}
					sortMethod={sortMethod}
					batchSameProduct={batchSameProduct}
					hidePickedItems={hidePickedItems}
					toggleShowAllSelections={toggleShowAllSelections}
					toggleHideImages={toggleHideImages}
					toggleHideHealthCompliance={toggleHideHealthCompliance}
					setSortMethod={setSortMethod}
					toggleBatchSameProduct={toggleBatchSameProduct}
					toggleHidePickedItems={toggleHidePickedItems}
					showAdvancedView={showAdvancedView}
					toggleShowAdvancedView={toggleShowAdvancedView}
					showSalesLastXDays={showSalesLastXDays}
					toggleShowSalesLastXDays={toggleShowSalesLastXDays}
					showSalesLastXDaysForPicklists={showSalesLastXDaysForPicklists}
					toggleShowSalesLastXDaysForPicklists={toggleShowSalesLastXDaysForPicklists}
					showPickOrderForPicklists={showPickOrderForPicklists}
					toggleShowPickOrderForPicklists={toggleShowPickOrderForPicklists}
					showQuantitySoldAfterRecalc={showQuantitySoldAfterRecalc}
					toggleShowQuantitySoldAfterRecalc={toggleShowQuantitySoldAfterRecalc}
					showExpiryDates={showExpiryDates}
					toggleShowExpiryDates={toggleShowExpiryDates}
					version={version}
					toggleDialogIsOpen={toggleDialogIsOpen}
					showExpiryDateButtons={showExpiryDateButtons}
					toggleShowExpiryDateButtons={toggleShowExpiryDateButtons}
				/>
			</Dialog>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	settingsButton: {
		paddingHorizontal: space.gutter,
		paddingBottom: space.sm,
	},
	groupName: {
		...type.sectionTitle,
		color: colors.text,
		paddingHorizontal: space.gutter,
		paddingTop: space.lg,
		paddingBottom: space.sm,
	},
	groupEmpty: {
		...type.bodySmall,
		color: colors.textSecondary,
		paddingHorizontal: space.gutter,
		paddingBottom: space.md,
	},
});

export default PlanogramDetail;
